import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { AuthContext, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { QHubBuddyLogo, QHubFooter } from "@/components/QHubBranding";
import PilotWizard from "@/components/PilotWizard";
import SecurityOverview from "@/components/SecurityOverview";
import CurriculumSection from "@/components/CurriculumSection";
import ContactSubmissionsSection from "@/components/ContactSubmissionsSection";
import MessagingInbox from "@/components/MessagingInbox";
import EscalationAnalyticsPanel from "@/components/EscalationAnalyticsPanel";
import DeleteSchoolDialog from "@/components/DeleteSchoolDialog";
import SchoolsTrashPanel from "@/components/SchoolsTrashPanel";
import {
  LogOut, Building2, Users, GraduationCap, BookOpen, DollarSign, Clock,
  AlertTriangle, ShieldAlert, RefreshCw, Trash2, KeyRound, Wrench, Database, Search, Bell, Sparkles,
  CheckCircle2, XCircle, Inbox, MessageSquare, Bot, Archive
} from "lucide-react";
import { toast } from "sonner";
import { IncidentOversightSection } from "@/components/IncidentOversightSection";

// ── helpers ──────────────────────────────────────────────────────────────
const fmtNum = (n) => Number(n || 0).toLocaleString("es-ES");
const fmtCost = (n) => `$${Number(n || 0).toFixed(3)}`;
const fmtMins = (sec) => `${Math.round((sec || 0) / 60)} min`;
const fmtDate = (iso) => {
  if (!iso) return "-";
  try { return new Date(iso).toLocaleDateString("es-ES"); } catch { return iso; }
};

// Standard plans by student range. Cap = $2.50/student/month at $0.000002/token
// => 1,250,000 tokens/student/month max.
const COST_PER_STUDENT_USD = 2.50;
const TOKENS_PER_STUDENT = 1250000; // = $2.50 / $0.000002
const PLANS = [
  { key: "esencial",      name: "🟢 Esencial",      tokens: 100 * TOKENS_PER_STUDENT,  maxStudents: 100, description: "0 – 100 estudiantes" },
  { key: "avanzado",      name: "🔵 Avanzado",      tokens: 200 * TOKENS_PER_STUDENT,  maxStudents: 200, description: "101 – 200 estudiantes" },
  { key: "premium",       name: "🟣 Premium",       tokens: 300 * TOKENS_PER_STUDENT,  maxStudents: 300, description: "201 – 300 estudiantes" },
  { key: "institucional", name: "🟠 Institucional", tokens: 400 * TOKENS_PER_STUDENT,  maxStudents: 400, description: "301 – 400 estudiantes" },
];

const StatCard = ({ icon: Icon, label, value, accent = "from-[#419cca] to-[#8b5cf6]", testId }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4" data-testid={testId}>
    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center shrink-0`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-900 truncate">{value}</p>
    </div>
  </div>
);

// ── Schools admin section (CRUD + invite) ────────────────────────────────
const SchoolsSection = ({ schools, refresh }) => {
  const [name, setName] = useState("");
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [statusUpdate, setStatusUpdate] = useState("active");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");

  useEffect(() => {
    if (!selectedSchoolId && schools?.[0]?.id) setSelectedSchoolId(schools[0].id);
  }, [schools, selectedSchoolId]);

  const selected = useMemo(
    () => schools.find((s) => s.id === selectedSchoolId) || null,
    [schools, selectedSchoolId]
  );

  const createSchool = async () => {
    if (!name.trim()) return;
    try {
      await axios.post(`${API}/admin/schools-v2`, { name: name.trim() }, { withCredentials: true });
      setName(""); toast.success("Escuela creada"); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear"); }
  };
  const updateStatus = async () => {
    if (!selectedSchoolId) return;
    try {
      await axios.patch(`${API}/admin/schools-v2/${selectedSchoolId}`, null,
        { params: { status: statusUpdate }, withCredentials: true });
      toast.success("Status actualizado"); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const inviteAdmin = async () => {
    if (!selectedSchoolId || !inviteEmail || !inviteName) return;
    try {
      await axios.post(`${API}/admin/schools-v2/${selectedSchoolId}/invite-admin`,
        { email: inviteEmail, name: inviteName }, { withCredentials: true });
      setInviteEmail(""); setInviteName(""); toast.success("Invitación enviada");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al invitar"); }
  };
  const toggleSchool = async (s) => {
    const willActivate = (s.status || (s.is_active === false ? "disabled" : "active")) !== "active";
    const next = willActivate ? "active" : "disabled";
    if (!willActivate) {
      if (!window.confirm(
        `¿Desactivar "${s.name}"?\n\nNingún usuario de esta escuela podrá iniciar sesión (excepto super_admin).`
      )) return;
    }
    try {
      await axios.patch(`${API}/admin/schools-v2/${s.id}`, null,
        { params: { status: next }, withCredentials: true });
      toast.success(willActivate ? `"${s.name}" activada` : `"${s.name}" desactivada`);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Escuelas</h2>
        <p className="text-sm text-gray-500">Crear, activar/desactivar e invitar administradores institucionales.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">Crear escuela</p>
          <div className="flex gap-3">
            <Input value={name} onChange={(e) => setName(e.target.value)}
                   placeholder="Nombre de escuela" data-testid="school-name-input" />
            <Button onClick={createSchool} disabled={!name.trim()} data-testid="create-school-btn">Crear</Button>
          </div>
        </div>
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">Seleccionar escuela</p>
          <Select value={selectedSchoolId} onValueChange={setSelectedSchoolId}>
            <SelectTrigger className="w-full" data-testid="school-select"><SelectValue placeholder="Selecciona" /></SelectTrigger>
            <SelectContent>
              {schools.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name} ({s.status || "active"})</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selected && (
            <div className="flex items-center gap-3">
              <Badge className={selected.status === "active"
                ? "bg-green-50 text-green-700"
                : "bg-gray-100 text-gray-700"}>{selected.status || "active"}</Badge>
              <Select value={statusUpdate} onValueChange={setStatusUpdate}>
                <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">active</SelectItem>
                  <SelectItem value="disabled">disabled</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={updateStatus} data-testid="update-status-btn">Actualizar</Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <p className="text-sm font-medium text-gray-700">Invitar admin institucional</p>
          <Input value={inviteName} onChange={(e) => setInviteName(e.target.value)}
                 placeholder="Nombre" data-testid="invite-name" />
          <Input value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                 placeholder="Email" type="email" data-testid="invite-email" />
          <Button onClick={inviteAdmin} disabled={!selectedSchoolId || !inviteEmail || !inviteName}
                  data-testid="send-invite-btn">Enviar invitación</Button>
          <p className="text-xs text-gray-400">Magic link por Brevo SMTP.</p>
        </div>
      </div>

      <div className="space-y-2 pt-2 border-t border-gray-100">
        <p className="text-sm font-medium text-gray-700">Todas las escuelas ({schools.length})</p>
        <p className="text-xs text-gray-500">Desactivar bloquea el login de todos los usuarios de la institución (excepto super_admin).</p>
        <div className="rounded-xl border border-gray-100 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acción</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {schools.map(s => {
                const status = s.status || (s.is_active === false ? "disabled" : "active");
                const isActive = status === "active";
                return (
                  <TableRow key={s.id} data-testid={`school-row-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell>
                      <Badge className={isActive ? "bg-green-50 text-green-700" : "bg-gray-200 text-gray-700"}
                             data-testid={`school-status-${s.id}`}>
                        {isActive ? "Activa" : "Desactivada"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleSchool(s)}
                          className={isActive ? "text-amber-600 gap-1" : "text-green-600 gap-1"}
                          data-testid={`toggle-school-${s.id}`}
                        >
                          {isActive ? <XCircle className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                          {isActive ? "Desactivar" : "Activar"}
                        </Button>
                        <DeleteSchoolDialog school={s} onDeleted={refresh} />
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {schools.length === 0 && (
                <TableRow><TableCell colSpan={3} className="text-center text-gray-400 py-6">Sin escuelas</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

// ── Subjects toggle cell ────────────────────────────────────────────────
const SUBJECTS_LIST = [
  { key: "comunicacion-lenguaje", label: "Len", title: "Lenguaje", color: "bg-red-500" },
  { key: "ciencia-tecnologia",    label: "Cie", title: "Ciencia", color: "bg-teal-500" },
  { key: "matematica-datos",      label: "Mat", title: "Matemática", color: "bg-violet-500" },
  { key: "ciudadania-valores",    label: "Ciu", title: "Ciudadanía", color: "bg-amber-500" },
  { key: "reto_avanzo",           label: "RA",  title: "Reto Avanzo (10° y 11°)", color: "bg-sky-500" },
];

// ── M1.2: Institutional products/nodes (independent of subjects) ────────
// Reto AVANZO stays a SUBJECT in SUBJECTS_LIST. Buddy Avanza is a distinct
// PRODUCT/NODE lifecycle, persisted at schools.products_enabled.*
const PRODUCTS_LIST = [
  { key: "buddy_avanza", label: "Buddy Avanza", color: "bg-indigo-500" },
];

const SubjectsCell = ({ row, onChange, saving }) => {
  const subjects = row.subjects_enabled || {};
  const toggle = (key) => {
    const next = { ...subjects, [key]: !subjects[key] };
    onChange(row.id, next);
  };
  return (
    <div className="flex gap-1.5 flex-wrap" data-testid={`subjects-cell-${row.id}`}>
      {SUBJECTS_LIST.map((s) => {
        const on = subjects[s.key];
        return (
          <button
            key={s.key}
            type="button"
            disabled={saving}
            onClick={() => toggle(s.key)}
            title={`${s.title}: ${on ? "ON" : "OFF"}`}
            data-testid={`toggle-${row.id}-${s.key}`}
            className={`px-2 py-1 rounded-md text-[10px] font-medium border transition-all ${
              on
                ? `${s.color} text-white border-transparent`
                : "bg-gray-50 text-gray-400 border-gray-200 line-through"
            }`}
          >
            {s.label}
          </button>
        );
      })}
    </div>
  );
};

// M1.2: renders products/nodes toggles (currently only Buddy Avanza).
// Persistence: schools.products_enabled.<key>. Independent of subjects.
const ProductsCell = ({ row, onChange, saving }) => {
  const products = row.products_enabled || {};
  const toggle = (key) => {
    const next = { ...products, [key]: !products[key] };
    onChange(row.id, next);
  };
  return (
    <div className="flex gap-1.5 flex-wrap" data-testid={`products-cell-${row.id}`}>
      {PRODUCTS_LIST.map((p) => {
        const on = !!products[p.key];
        return (
          <button
            key={p.key}
            type="button"
            disabled={saving}
            onClick={() => toggle(p.key)}
            title={`${p.label}: ${on ? "ON" : "OFF"}`}
            data-testid={`toggle-product-${row.id}-${p.key}`}
            className={`px-2 py-1 rounded-md text-[11px] font-medium border transition-all ${
              on
                ? `${p.color} text-white border-transparent`
                : "bg-gray-50 text-gray-400 border-gray-200 line-through"
            }`}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
};

// ── Per-school detail table (drill-down) ─────────────────────────────────
const QUOTA_STATUS_STYLES = {
  unlimited: { dot: "bg-gray-300", text: "text-gray-500", label: "Ilimitado" },
  ok:        { dot: "bg-emerald-500", text: "text-emerald-700", label: "OK" },
  warning:   { dot: "bg-amber-500", text: "text-amber-700", label: "Alerta" },
  exceeded:  { dot: "bg-red-500", text: "text-red-700", label: "Excedido" },
};

const QuotaCell = ({ row, onEdit, saving }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(row.monthly_quota_tokens || 0));
  useEffect(() => { setValue(String(row.monthly_quota_tokens || 0)); }, [row.monthly_quota_tokens]);

  const meta = QUOTA_STATUS_STYLES[row.quota_status] || QUOTA_STATUS_STYLES.unlimited;
  const quota = row.monthly_quota_tokens || 0;
  const used = row.month_tokens_used || 0;
  const pct = row.quota_usage_percent;

  const barWidth = pct == null ? 0 : Math.min(100, pct);
  const barColor = row.quota_status === "exceeded" ? "bg-red-500"
    : row.quota_status === "warning" ? "bg-amber-500"
    : row.quota_status === "ok" ? "bg-emerald-500" : "bg-gray-300";

  const save = async () => {
    const v = parseInt(value, 10);
    if (Number.isNaN(v) || v < 0) { toast.error("Cupo inválido"); return; }
    await onEdit(row.id, v);
    setEditing(false);
  };

  const applyPlan = async (planKey) => {
    const plan = PLANS.find(p => p.key === planKey);
    if (!plan) return;
    if (plan.tokens === (row.monthly_quota_tokens || 0)) return; // no-op if same
    await onEdit(row.id, plan.tokens);
  };

  // Identify current plan match (exact match)
  const matchedPlan = PLANS.find(p => p.tokens === quota);
  const currentPlanLabel = matchedPlan ? matchedPlan.name : (quota > 0 ? "Personalizado" : "⚪ Ilimitado");

  if (editing) {
    return (
      <div className="flex gap-2 items-center">
        <Input className="h-8 w-28" value={value} onChange={(e) => setValue(e.target.value)}
               type="number" min="0" data-testid={`quota-input-${row.id}`} />
        <Button size="sm" variant="outline" onClick={save} disabled={saving} data-testid={`quota-save-${row.id}`}>OK</Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>X</Button>
      </div>
    );
  }

  return (
    <div className="space-y-1 min-w-[210px]" data-testid={`quota-cell-${row.id}`}>
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className={`flex items-center gap-1.5 ${meta.text}`}>
          <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
          {meta.label}
        </span>
        <button onClick={() => setEditing(true)} className="text-[10px] text-blue-600 hover:underline" data-testid={`quota-edit-${row.id}`}>
          editar manual
        </button>
      </div>
      <div className="text-xs text-gray-700">
        {quota > 0 ? (
          <>{fmtNum(used)} / {fmtNum(quota)} {pct != null ? `(${pct}%)` : ""}</>
        ) : (
          <span className="text-gray-400">{fmtNum(used)} este mes · sin cupo</span>
        )}
      </div>
      {quota > 0 && (
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className={`h-full ${barColor} transition-all`} style={{ width: `${barWidth}%` }} />
        </div>
      )}
      <Select value={matchedPlan?.key || ""} onValueChange={applyPlan}>
        <SelectTrigger className="h-7 text-xs" data-testid={`plan-select-${row.id}`}>
          <SelectValue placeholder={currentPlanLabel} />
        </SelectTrigger>
        <SelectContent>
          {PLANS.map((p) => (
            <SelectItem key={p.key} value={p.key}>
              <span className="font-medium">{p.name}</span>
              <span className="text-gray-500 ml-2">{p.tokens > 0 ? `${fmtNum(p.tokens)} tokens` : "sin límite"}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// ── M3.1 · Roster Prefix (super_admin only, SET-ONCE) ──────────────
const RosterPrefixCell = ({ row, onSet, saving }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const existing = (row.roster_prefix || "").trim();

  const normalize = (raw) =>
    (raw || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "")
      .replace(/[^A-Z0-9._-]/g, "");

  const save = async () => {
    const n = normalize(value);
    if (n.length < 1 || n.length > 16) {
      toast.error("El prefijo debe tener 1–16 caracteres válidos.");
      return;
    }
    await onSet(row.id, n);
    setEditing(false);
    setValue("");
  };

  // Already set → readonly display
  if (existing) {
    return (
      <div className="text-xs font-mono text-gray-800"
           data-testid={`roster-prefix-${row.id}`}>
        <span className="px-1.5 py-0.5 bg-gray-100 rounded">{existing}</span>
        <div className="text-[10px] text-gray-400 mt-0.5">
          set-once · inmutable
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="flex gap-1 items-center" data-testid={`roster-prefix-${row.id}`}>
        <Input
          className="h-7 w-20 text-xs font-mono"
          value={value}
          onChange={(e) => setValue(e.target.value.toUpperCase())}
          placeholder="HISP"
          maxLength={16}
          data-testid={`roster-prefix-input-${row.id}`}
        />
        <Button size="sm" variant="outline" onClick={save} disabled={saving}
                data-testid={`roster-prefix-save-${row.id}`}>OK</Button>
        <Button size="sm" variant="ghost"
                onClick={() => { setEditing(false); setValue(""); }}>X</Button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="text-[11px] text-blue-600 hover:underline"
      data-testid={`roster-prefix-set-${row.id}`}
    >
      Establecer…
    </button>
  );
};



const SchoolBreakdown = ({ rows, onPick, refresh }) => {
  const [saving, setSaving] = useState(false);
  const updateQuota = async (schoolId, quota) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/schools-v2/${schoolId}/quota`,
        { monthly_quota_tokens: quota }, { withCredentials: true });
      toast.success("Cupo mensual actualizado");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error actualizando cupo");
    } finally { setSaving(false); }
  };

  const updateSubjects = async (schoolId, subjects_enabled) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/schools-v2/${schoolId}/subjects`,
        { subjects_enabled }, { withCredentials: true });
      toast.success("Materias actualizadas");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error actualizando materias");
    } finally { setSaving(false); }
  };

  // M1.2: independent of subjects. Persists to products_enabled.*
  const updateProducts = async (schoolId, products_enabled) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/schools-v2/${schoolId}/products`,
        { products_enabled }, { withCredentials: true });
      toast.success("Productos actualizados");
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error actualizando productos");
    } finally { setSaving(false); }
  };

  // M3.1: set-once roster prefix. Backend enforces super_admin + set-once.
  const setRosterPrefix = async (schoolId, roster_prefix) => {
    if (!window.confirm(
      `¿Establecer roster_prefix="${roster_prefix}" para esta institución?\n\n` +
      `Este valor es INMUTABLE una vez establecido y se usará como prefijo ` +
      `de todos los usernames generados por importación de roster (M3.1).`
    )) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/schools-v2/${schoolId}/roster-prefix`, null,
        { params: { roster_prefix }, withCredentials: true });
      toast.success(`Roster prefix "${roster_prefix}" establecido`);
      await refresh();
    } catch (e) {
      const d = e.response?.data?.detail || "";
      if (d === "roster_prefix_already_set") {
        toast.error("Ya existe un roster_prefix; es inmutable.");
      } else if (d === "roster_prefix_collision") {
        toast.error("Ese roster_prefix ya está en uso por otra institución.");
      } else if (d === "roster_prefix_invalid_length") {
        toast.error("Longitud inválida (1–16 caracteres alfanuméricos).");
      } else {
        toast.error(d || "Error estableciendo roster_prefix");
      }
    } finally { setSaving(false); }
  };

  const toggleSchoolStatus = async (s) => {
    const isActive = (s.status || "active") === "active";
    const next = isActive ? "disabled" : "active";
    if (isActive) {
      if (!window.confirm(
        `¿Desactivar "${s.name}"?\n\nNingún usuario de esta escuela podrá iniciar sesión (excepto super_admin).`
      )) return;
    } else if (!window.confirm(`¿Reactivar "${s.name}"?`)) {
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/schools-v2/${s.id}`, null,
        { params: { status: next }, withCredentials: true });
      toast.success(isActive ? `"${s.name}" desactivada` : `"${s.name}" reactivada`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error actualizando estado");
    } finally { setSaving(false); }
  };

  const toggleB2C = async (s) => {
    const willEnable = !s.b2c_module_enabled;
    if (willEnable && !window.confirm(
      `¿Habilitar módulo de compras B2C para "${s.name}"?\n\nLa página pública /comprar mostrará esta institución y los compradores que paguen serán inscritos automáticamente como estudiantes.`
    )) return;
    if (!willEnable && !window.confirm(
      `¿Deshabilitar módulo de compras para "${s.name}"?\n\nDejará de aparecer en /comprar.`
    )) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/schools-v2/${s.id}/b2c-toggle`,
        { enabled: willEnable }, { withCredentials: true });
      toast.success(willEnable ? `Compras B2C activadas en "${s.name}"` : `Compras B2C desactivadas en "${s.name}"`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error actualizando módulo B2C");
    } finally { setSaving(false); }
  };

  const warningSchools = rows.filter(r => r.quota_status === "warning" || r.quota_status === "exceeded");

  return (
    <div className="space-y-4">
      {warningSchools.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-center gap-3" data-testid="quota-warning-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <div className="flex-1 text-sm">
            <p className="font-semibold text-amber-900">Cupo mensual cerca o sobrepasado</p>
            <p className="text-amber-700">
              {warningSchools.map(s => `${s.name} (${s.quota_usage_percent}%)`).join(" · ")}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 p-4" data-testid="plans-legend">
        <div className="flex items-baseline justify-between mb-2 flex-wrap gap-1">
          <p className="text-xs font-semibold text-gray-700">Planes estándar por rango de estudiantes</p>
          <p className="text-[11px] text-gray-500">
            Tope: <span className="font-medium">${COST_PER_STUDENT_USD.toFixed(2)}/estudiante/mes</span> ·
            <span className="ml-1">{fmtNum(TOKENS_PER_STUDENT)} tokens/estudiante</span>
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {PLANS.map(p => {
            const maxCost = COST_PER_STUDENT_USD * (p.maxStudents || 0);
            return (
              <div key={p.key} className="border border-gray-100 rounded-lg p-2">
                <p className="font-medium">{p.name}</p>
                <p className="text-[11px] text-gray-600">{p.description}</p>
                <p className="text-gray-500 mt-0.5">{fmtNum(p.tokens)} tokens/mes</p>
                <p className="text-[11px] text-green-700 font-medium mt-0.5">Costo máx: ${maxCost.toFixed(0)}</p>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">Desempeño por escuela</h2>
          <p className="text-sm text-gray-500">Usuarios, evidencias, uso del tutor, costo y cupo mensual de tokens.</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Escuela</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Compras B2C</TableHead>
              <TableHead>Materias / Áreas</TableHead>
              <TableHead>Productos / Nodos</TableHead>
              <TableHead>Roster Prefix</TableHead>
              <TableHead>Estudiantes</TableHead>
              <TableHead>Docentes</TableHead>
              <TableHead>Padres</TableHead>
              <TableHead>Evidencias</TableHead>
              <TableHead>Sesiones</TableHead>
              <TableHead>Tokens (total)</TableHead>
              <TableHead>Cupo / Uso mes</TableHead>
              <TableHead>Costo</TableHead>
              <TableHead>Tiempo</TableHead>
              <TableHead>Patrón IA</TableHead>
              <TableHead className="text-right">Usuarios</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s) => (
              <TableRow key={s.id} data-testid={`school-row-${s.id}`}>
                <TableCell className="font-medium">{s.name}</TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-1">
                    <Badge className={s.status === "active" ? "bg-green-50 text-green-700" : "bg-gray-200 text-gray-700"}
                           data-testid={`school-status-${s.id}`}>
                      {s.status === "active" ? "Activa" : "Desactivada"}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleSchoolStatus(s)}
                      disabled={saving}
                      className={`h-6 px-2 text-[11px] gap-1 ${s.status === "active" ? "text-amber-600 hover:text-amber-700" : "text-green-600 hover:text-green-700"}`}
                      data-testid={`toggle-school-${s.id}`}
                    >
                      {s.status === "active"
                        ? <><XCircle className="w-3 h-3" />Desactivar</>
                        : <><CheckCircle2 className="w-3 h-3" />Activar</>}
                    </Button>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-1">
                    <Badge
                      className={s.b2c_module_enabled ? "bg-indigo-50 text-indigo-700" : "bg-gray-100 text-gray-500"}
                      data-testid={`school-b2c-status-${s.id}`}
                    >
                      {s.b2c_module_enabled ? "Habilitado" : "Deshabilitado"}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleB2C(s)}
                      disabled={saving}
                      className={`h-6 px-2 text-[11px] gap-1 ${s.b2c_module_enabled ? "text-amber-600 hover:text-amber-700" : "text-indigo-600 hover:text-indigo-700"}`}
                      data-testid={`toggle-b2c-${s.id}`}
                    >
                      {s.b2c_module_enabled
                        ? <><XCircle className="w-3 h-3" />Quitar de /comprar</>
                        : <><CheckCircle2 className="w-3 h-3" />Habilitar /comprar</>}
                    </Button>
                  </div>
                </TableCell>
                <TableCell>
                  <SubjectsCell row={s} onChange={updateSubjects} saving={saving} />
                </TableCell>
                <TableCell>
                  <ProductsCell row={s} onChange={updateProducts} saving={saving} />
                </TableCell>
                <TableCell>
                  <RosterPrefixCell row={s} onSet={setRosterPrefix} saving={saving} />
                </TableCell>
                <TableCell>{fmtNum(s.students)}</TableCell>
                <TableCell>{fmtNum(s.teachers)}</TableCell>
                <TableCell>{fmtNum(s.parents)}</TableCell>
                <TableCell>{fmtNum(s.evidences)}</TableCell>
                <TableCell>{fmtNum(s.sessions)}</TableCell>
                <TableCell>{fmtNum(s.tokens_used)}</TableCell>
                <TableCell>
                  <QuotaCell row={s} onEdit={updateQuota} saving={saving} />
                </TableCell>
                <TableCell className="text-green-700 font-medium">{fmtCost(s.estimated_cost)}</TableCell>
                <TableCell>{fmtMins(s.session_seconds)}</TableCell>
                <TableCell>
                  {s.paste_suspicions > 0 ? (
                    <Badge className="bg-amber-50 text-amber-700 border border-amber-200">{s.paste_suspicions}</Badge>
                  ) : <span className="text-gray-300">0</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => onPick(s)}
                            className="text-[#419cca]" data-testid={`pick-school-${s.id}`}>
                      Ver usuarios
                    </Button>
                    <DeleteSchoolDialog school={s} onDeleted={refresh} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ── Users management (global) ────────────────────────────────────────────
const UsersSection = ({ schools, filterSchoolId, setFilterSchoolId, currentUserId }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [q, setQ] = useState("");
  // M3.2 · bulk selection state.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkMode, setBulkMode] = useState(null); // "soft" | "hard" | null
  const [bulkConfirmPhrase, setBulkConfirmPhrase] = useState("");
  const [bulkAck, setBulkAck] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterSchoolId && filterSchoolId !== "all") params.school_id = filterSchoolId;
      if (roleFilter !== "all") params.role = roleFilter;
      const res = await axios.get(`${API}/admin/users`, { params, withCredentials: true });
      setUsers(res.data || []);
    } catch (e) { toast.error("Error al cargar usuarios"); }
    finally { setLoading(false); }
  }, [filterSchoolId, roleFilter]);

  useEffect(() => { fetch(); }, [fetch]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(u =>
      (u.name || "").toLowerCase().includes(needle) ||
      (u.email || "").toLowerCase().includes(needle) ||
      (u.username || "").toLowerCase().includes(needle)
    );
  }, [users, q]);

  const schoolNameById = useMemo(() => {
    const m = {}; (schools || []).forEach(s => { m[s.id] = s.name; }); return m;
  }, [schools]);

  const resetPwd = async (email) => {
    const pwd = window.prompt(`Nueva contraseña para ${email}:`);
    if (!pwd) return;
    try {
      await axios.put(`${API}/admin/reset-user-password`, { email, password: pwd }, { withCredentials: true });
      toast.success(`Contraseña restablecida para ${email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const removeUser = async (email, role) => {
    if (!window.confirm(`¿Eliminar definitivamente a ${email} (${role})? Se borrarán evidencias, sesiones y perfil.`)) return;
    try {
      await axios.delete(`${API}/admin/delete-user`, { data: { email, confirm: true }, withCredentials: true });
      toast.success(`${email} eliminado`); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const toggleActive = async (u) => {
    const willActivate = u.is_active === false || u.suspended === true;
    const label = u.username || u.email || u.id;
    const action = willActivate ? "Activar" : "Desactivar";
    let reason = "manual_admin";
    if (!willActivate) {
      const r = window.prompt(`Motivo para desactivar ${label}:`, "manual_admin");
      if (r === null) return;
      reason = (r.trim() || "manual_admin").slice(0, 100);
    } else if (!window.confirm(`¿${action} a ${label}?`)) {
      return;
    }
    try {
      await axios.patch(
        `${API}/admin/users-v2/${u.id}/active`,
        { active: willActivate, reason },
        { withCredentials: true }
      );
      toast.success(willActivate ? `${label} activado` : `${label} desactivado`);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  // ── M3.2 · bulk selection helpers ──
  const selectableIds = useMemo(
    () => filtered
      .filter(u => u.role !== "super_admin" && u.id !== currentUserId)
      .map(u => u.id),
    [filtered, currentUserId],
  );
  const allFilteredSelected = selectableIds.length > 0
    && selectableIds.every(id => selectedIds.has(id));
  const someFilteredSelected = !allFilteredSelected
    && selectableIds.some(id => selectedIds.has(id));

  const toggleSelectOne = (uid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };
  const toggleSelectAllFiltered = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        selectableIds.forEach(id => next.delete(id));
      } else {
        selectableIds.forEach(id => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectedCount = selectedIds.size;
  const bulkOpen = bulkMode !== null;
  const requiredPhrase = `ELIMINAR ${selectedCount} USUARIOS`;

  const closeBulk = () => {
    if (bulkBusy) return;
    setBulkMode(null);
    setBulkConfirmPhrase("");
    setBulkAck(false);
  };

  const runBulkSoft = async () => {
    if (!bulkAck || selectedCount === 0) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedIds);
      const filtersCtx = { school_id: filterSchoolId || "all",
                            role: roleFilter, q };
      const res = await axios.post(
        `${API}/admin/users/bulk-deactivate`,
        { user_ids: ids, confirm: true, reason: "super_admin_ui",
          filters_context: filtersCtx },
        { withCredentials: true },
      );
      const d = res.data || {};
      toast.success(
        `Desactivados: ${d.processed_count}. Omitidos: ` +
        `${(d.skipped_not_found || 0) + (d.skipped_self || 0)
          + (d.skipped_protected_role || 0)}.`
      );
      clearSelection();
      closeBulk();
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error en desactivación masiva");
    } finally {
      setBulkBusy(false);
    }
  };
  const runBulkHard = async () => {
    if (bulkConfirmPhrase !== requiredPhrase || selectedCount === 0) return;
    setBulkBusy(true);
    try {
      const ids = Array.from(selectedIds);
      const filtersCtx = { school_id: filterSchoolId || "all",
                            role: roleFilter, q };
      const res = await axios.post(
        `${API}/admin/users/bulk-delete`,
        { user_ids: ids, confirm: true,
          confirmation_phrase: bulkConfirmPhrase,
          reason: "super_admin_ui",
          filters_context: filtersCtx },
        { withCredentials: true },
      );
      const d = res.data || {};
      toast.success(
        `Eliminados: ${d.processed_count}. Omitidos: ` +
        `${(d.skipped_not_found || 0) + (d.skipped_self || 0)
          + (d.skipped_protected_role || 0)}.`
      );
      clearSelection();
      closeBulk();
      fetch();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "confirmation_phrase_mismatch") {
        toast.error("Frase de confirmación incorrecta.");
      } else if (detail === "no_valid_targets_after_resolution") {
        toast.error("No hay usuarios válidos en la selección.");
      } else {
        toast.error(detail || "Error en eliminación masiva");
      }
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
      <div className="p-6 border-b border-gray-100 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Gestión global de usuarios</h2>
            <p className="text-sm text-gray-500">Filtra por escuela y rol. Restablece contraseña o elimina.</p>
          </div>
          <Button variant="outline" onClick={fetch} disabled={loading} className="gap-2" data-testid="reload-users-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />Recargar
          </Button>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <Select value={filterSchoolId || "all"} onValueChange={setFilterSchoolId}>
            <SelectTrigger data-testid="users-school-filter"><SelectValue placeholder="Todas las escuelas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las escuelas</SelectItem>
              {(schools || []).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger data-testid="users-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los roles</SelectItem>
              <SelectItem value="student">student</SelectItem>
              <SelectItem value="parent">parent</SelectItem>
              <SelectItem value="teacher">teacher</SelectItem>
              <SelectItem value="institutional_admin">institutional_admin</SelectItem>
              <SelectItem value="super_admin">super_admin</SelectItem>
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input className="pl-9" placeholder="Buscar nombre / email / usuario"
                   value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search" />
          </div>
        </div>
        <p className="text-xs text-gray-400">{filtered.length} usuario(s) mostrado(s).</p>

        {/* ── M3.2 · Bulk action bar ── */}
        <div className="flex items-center gap-3 flex-wrap pt-1">
          <label
            className="flex items-center gap-2 text-sm text-gray-700"
            data-testid="bulk-select-all-label"
          >
            <Checkbox
              checked={allFilteredSelected ? true : (someFilteredSelected ? "indeterminate" : false)}
              onCheckedChange={toggleSelectAllFiltered}
              disabled={selectableIds.length === 0}
              data-testid="bulk-select-all"
            />
            Seleccionar todos los filtrados
            <span className="text-xs text-gray-400">
              ({selectableIds.length} elegibles)
            </span>
          </label>
          <div className="flex-1" />
          <span
            className="text-sm text-gray-600"
            data-testid="bulk-selected-count"
          >
            {selectedCount} seleccionado(s)
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={clearSelection}
            disabled={selectedCount === 0}
            data-testid="bulk-clear"
          >
            Limpiar
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-amber-700 border-amber-300"
            onClick={() => { setBulkAck(false); setBulkMode("soft"); }}
            disabled={selectedCount === 0}
            data-testid="bulk-soft-btn"
          >
            <XCircle className="w-3.5 h-3.5 mr-1" />
            Desactivar seleccionados
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-red-700 border-red-300"
            onClick={() => { setBulkConfirmPhrase(""); setBulkMode("hard"); }}
            disabled={selectedCount === 0}
            data-testid="bulk-hard-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            Eliminar permanentemente
          </Button>
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10"></TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead>Usuario/Email</TableHead>
            <TableHead>Rol</TableHead>
            <TableHead>Grado</TableHead>
            <TableHead>Escuela</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="text-right">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.slice(0, 500).map(u => {
            const isInactive = u.is_active === false || u.suspended === true;
            const isHacker = u.suspended_reason === "points_manipulation_detected";
            return (
            <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
              <TableCell className="w-10">
                {u.role !== "super_admin" && u.id !== currentUserId && (
                  <Checkbox
                    checked={selectedIds.has(u.id)}
                    onCheckedChange={() => toggleSelectOne(u.id)}
                    data-testid={`bulk-select-${u.id}`}
                  />
                )}
              </TableCell>
              <TableCell className="font-medium">{u.name || "-"}</TableCell>
              <TableCell className="text-sm">
                {u.username ? <span className="font-mono">{u.username}</span> : <span className="text-gray-500">{u.email}</span>}
              </TableCell>
              <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
              <TableCell>{u.grade || "-"}</TableCell>
              <TableCell className="text-sm text-gray-600">{schoolNameById[u.school_id] || "-"}</TableCell>
              <TableCell>
                {isInactive ? (
                  <Badge
                    className={isHacker ? "bg-red-100 text-red-700" : "bg-gray-200 text-gray-700"}
                    data-testid={`user-status-${u.id}`}
                  >
                    {isHacker ? "BLOQUEADO" : "Inactivo"}
                  </Badge>
                ) : (
                  <Badge className="bg-green-50 text-green-700" data-testid={`user-status-${u.id}`}>
                    Activo
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleActive(u)}
                    disabled={u.role === "super_admin"}
                    className={isInactive ? "text-green-600 gap-1" : "text-amber-600 gap-1"}
                    data-testid={`toggle-active-${u.id}`}
                    title={u.role === "super_admin" ? "No se puede desactivar super_admin desde la UI" : ""}
                  >
                    {isInactive ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                    {isInactive ? "Activar" : "Desactivar"}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => resetPwd(u.email)}
                          className="text-blue-600 gap-1" data-testid={`reset-${u.id}`}>
                    <KeyRound className="w-3.5 h-3.5" /> Pwd
                  </Button>
                  {u.role !== "super_admin" && (
                    <Button variant="ghost" size="sm" onClick={() => removeUser(u.email, u.role)}
                            className="text-red-600 gap-1" data-testid={`del-${u.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
            );
          })}
          {filtered.length === 0 && !loading && (
            <TableRow><TableCell colSpan={8} className="text-center text-gray-400 py-8">Sin resultados</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      {/* ── M3.2 · Bulk action dialog ── */}
      <AlertDialog open={bulkOpen} onOpenChange={(v) => { if (!v) closeBulk(); }}>
        <AlertDialogContent data-testid="bulk-dialog">
          {bulkMode === "soft" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Desactivar {selectedCount} usuario(s)</AlertDialogTitle>
                <AlertDialogDescription>
                  Se marcarán como inactivos y sus sesiones activas serán
                  revocadas. El historial académico se conserva. Los
                  super administradores y tu propia cuenta serán omitidos
                  automáticamente.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <label className="flex items-start gap-3 py-3 text-sm text-gray-700">
                <Checkbox
                  checked={bulkAck}
                  onCheckedChange={(v) => setBulkAck(v === true)}
                  data-testid="bulk-soft-ack"
                />
                <span>
                  Confirmo que quiero desactivar a {selectedCount} usuario(s)
                  y revocar sus sesiones.
                </span>
              </label>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="bulk-cancel">Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); runBulkSoft(); }}
                  disabled={!bulkAck || bulkBusy}
                  data-testid="bulk-soft-confirm"
                >
                  {bulkBusy ? "Desactivando..." : "Desactivar"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {bulkMode === "hard" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle className="text-red-700">
                  Eliminar permanentemente {selectedCount} usuario(s)
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción es <strong>irreversible</strong>. Se eliminarán:
                  usuarios, sesiones, perfiles de estudiante, asignaciones
                  docente-estudiante, evidencias, puntos y sesiones de chat
                  vinculadas. Se preservan importaciones de roster y auditoría.
                  Los super administradores y tu propia cuenta serán omitidos.
                  <br /><br />
                  Para confirmar, escribe exactamente:
                  <br />
                  <code className="bg-gray-100 px-2 py-0.5 rounded font-mono">
                    {requiredPhrase}
                  </code>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <Input
                value={bulkConfirmPhrase}
                onChange={(e) => setBulkConfirmPhrase(e.target.value)}
                placeholder={requiredPhrase}
                autoFocus
                data-testid="bulk-hard-phrase"
                className="font-mono"
              />
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="bulk-cancel">Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(e) => { e.preventDefault(); runBulkHard(); }}
                  disabled={bulkConfirmPhrase !== requiredPhrase || bulkBusy}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="bulk-hard-confirm"
                >
                  {bulkBusy ? "Eliminando..." : "Eliminar permanentemente"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ── Maintenance section (seeds + backfills) ──────────────────────────────
const MaintenanceSection = () => {
  const [running, setRunning] = useState({});
  const SEED_HEADERS = { "X-Seed-Token": "qhub-seed-2024" };

  const run = async (key, label, fn) => {
    if (running[key]) return;
    if (!window.confirm(`¿Ejecutar "${label}"?`)) return;
    setRunning((p) => ({ ...p, [key]: true }));
    try {
      const res = await fn();
      toast.success(`${label} ok`, { description: JSON.stringify(res.data).slice(0, 200) });
    } catch (e) {
      toast.error(`${label} falló`, { description: e.response?.data?.detail || String(e) });
    } finally {
      setRunning((p) => ({ ...p, [key]: false }));
    }
  };

  const items = [
    { key: "cq", label: "Seed Colegio Quiroz (background)", icon: Database,
      fn: () => axios.post(`${API}/admin/force-seed-cq`, {}, { headers: SEED_HEADERS, withCredentials: true }) },
    { key: "ebm", label: "Seed Escuela Bilingüe Maquilishuat", icon: Database,
      fn: () => axios.post(`${API}/admin/force-seed-ebm`, {}, { headers: SEED_HEADERS, withCredentials: true }) },
    { key: "full", label: "Seed completo (demo + currículum)", icon: Database,
      fn: () => axios.post(`${API}/admin/force-seed`, {}, { headers: SEED_HEADERS, withCredentials: true }) },
    { key: "backfill_ev", label: "Backfill evidencias legacy", icon: Wrench,
      fn: () => axios.post(`${API}/admin/backfill-evidence`, {}, { headers: SEED_HEADERS, withCredentials: true }) },
    { key: "migrate", label: "Migrar hashes de contraseñas", icon: Wrench,
      fn: () => axios.post(`${API}/admin/migrate-passwords`, {}, { headers: SEED_HEADERS, withCredentials: true }) },
    { key: "backfill_chat", label: "Backfill sesiones de chat", icon: Wrench,
      fn: () => axios.post(`${API}/admin/backfill-chat-sessions`, {}, { headers: SEED_HEADERS, withCredentials: true }) },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shrink-0">
          <Wrench className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Mantenimiento</h2>
          <p className="text-sm text-gray-500">Disparadores manuales. Cuidado: afectan la base de datos.</p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(({ key, label, icon: Icon, fn }) => (
          <div key={key} className="border border-gray-100 rounded-xl p-4 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <Icon className="w-5 h-5 text-amber-500" />
              <p className="font-medium text-gray-800 text-sm">{label}</p>
            </div>
            <Button size="sm" variant="outline" disabled={running[key]}
                    onClick={() => run(key, label, fn)} data-testid={`maint-${key}`}>
              {running[key] ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Ejecutar"}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Quota alerts (super admin only) ───────────────────────────────────────
const QuotaAlertsSection = ({ schools }) => {
  const [data, setData] = useState({ alerts: [], by_school: [] });
  const [loading, setLoading] = useState(false);
  const [schoolId, setSchoolId] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (schoolId !== "all") params.school_id = schoolId;
      const res = await axios.get(`${API}/admin/quota-alerts`, { params, withCredentials: true });
      setData(res.data || { alerts: [], by_school: [] });
    } catch { toast.error("Error al cargar alertas de cupo"); }
    finally { setLoading(false); }
  }, [schoolId]);

  useEffect(() => { load(); }, [load]);

  const thColor = (t) => t === 100 ? "bg-red-50 text-red-700 border-red-200"
    : t === 90 ? "bg-orange-50 text-orange-700 border-orange-200"
    : "bg-amber-50 text-amber-700 border-amber-200";

  return (
    <div className="space-y-4" data-testid="quota-alerts-section">
      <div className="bg-white rounded-2xl border border-gray-100 p-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Alertas de cupo (privadas)</h2>
          <p className="text-sm text-gray-500">Se generan cuando una escuela alcanza 80% / 90% / 100% del cupo del mes. También recibes correo en tu cuenta de super admin.</p>
        </div>
        <div className="flex gap-3 items-center">
          <Select value={schoolId} onValueChange={setSchoolId}>
            <SelectTrigger className="w-[260px]" data-testid="quota-school-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las escuelas</SelectItem>
              {(schools || []).map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} disabled={loading} className="gap-2" data-testid="reload-quota-alerts">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />Recargar
          </Button>
        </div>
      </div>

      {data.by_school?.length > 0 && (
        <div className="grid md:grid-cols-3 gap-3">
          {data.by_school.map(s => (
            <div key={s.school_id} className="bg-white rounded-2xl border border-gray-100 p-4">
              <p className="font-semibold text-gray-900">{s.school_name}</p>
              <div className="flex gap-2 mt-2 text-xs">
                <Badge className="bg-amber-50 text-amber-700">80%: {s.count_80}</Badge>
                <Badge className="bg-orange-50 text-orange-700">90%: {s.count_90}</Badge>
                <Badge className="bg-red-50 text-red-700">100%: {s.count_100}</Badge>
              </div>
              <p className="text-[11px] text-gray-400 mt-2">Último: {fmtDate(s.last_ts)}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Mes</TableHead>
              <TableHead>Escuela</TableHead>
              <TableHead>Umbral</TableHead>
              <TableHead>Tokens / Cupo</TableHead>
              <TableHead>%</TableHead>
              <TableHead>Costo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.alerts.map(a => (
              <TableRow key={a.id} data-testid={`quota-alert-row-${a.id}`}>
                <TableCell className="text-xs text-gray-500">{fmtDate(a.created_at)}</TableCell>
                <TableCell className="text-xs">{a.year_month}</TableCell>
                <TableCell className="font-medium">{a.school_name}</TableCell>
                <TableCell>
                  <Badge className={`border ${thColor(a.threshold)}`}>≥ {a.threshold}%</Badge>
                </TableCell>
                <TableCell className="text-sm">{fmtNum(a.tokens_used)} / {fmtNum(a.tokens_quota)}</TableCell>
                <TableCell className="font-semibold">{a.percent}%</TableCell>
                <TableCell className="text-green-700">{fmtCost(a.estimated_cost)}</TableCell>
              </TableRow>
            ))}
            {data.alerts.length === 0 && !loading && (
              <TableRow><TableCell colSpan={7} className="text-center text-gray-400 py-8">Aún sin alertas registradas. Buenas noticias.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────
const SuperAdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [filterSchoolId, setFilterSchoolId] = useState("all");
  const isSuperAdmin = user?.role === "super_admin";

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/super-stats`, { withCredentials: true });
      setStats(res.data);
    } catch (e) {
      toast.error("Error al cargar métricas globales");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!isSuperAdmin) { navigate("/dashboard"); return; }
    fetchStats();
  }, [user, isSuperAdmin, navigate, fetchStats]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.warn("Logout request failed (continuing anyway):", e);
    }
    navigate("/login");
  };

  const onPickSchool = (s) => { setFilterSchoolId(s.id); setActiveTab("users"); };

  if (loading && !stats) {
    return (
      <div className="min-h-screen bg-mesh flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#419cca] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const totals = stats?.totals || {};
  const schools = stats?.schools || [];

  return (
    <div className="min-h-screen flex flex-col bg-mesh" data-testid="super-admin-dashboard">
      <header className="bg-white/80 backdrop-blur border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <QHubBuddyLogo size="default" />
            <Badge className="bg-purple-50 text-purple-700">Super Admin</Badge>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={fetchStats} className="gap-2 text-gray-500" data-testid="refresh-stats-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />Actualizar
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="flex items-center gap-2 text-gray-500 hover:text-gray-900" data-testid="logout-btn">
              <LogOut className="w-4 h-4" /> Cerrar sesión
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full space-y-6">
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard icon={Building2} label="Escuelas" value={fmtNum(totals.schools)} accent="from-indigo-500 to-violet-500" testId="stat-schools" />
          <StatCard icon={Users} label="Usuarios" value={fmtNum(totals.users)} accent="from-blue-500 to-cyan-500" testId="stat-users" />
          <StatCard icon={GraduationCap} label="Estudiantes" value={fmtNum(totals.students)} accent="from-emerald-500 to-teal-500" testId="stat-students" />
          <StatCard icon={BookOpen} label="Evidencias" value={fmtNum(totals.evidences)} accent="from-fuchsia-500 to-pink-500" testId="stat-evidences" />
          <StatCard icon={DollarSign} label="Costo total" value={fmtCost(totals.estimated_cost)} accent="from-green-500 to-emerald-600" testId="stat-cost" />
          <StatCard icon={Clock} label="Tiempo tutor" value={fmtMins(totals.session_seconds)} accent="from-amber-500 to-orange-500" testId="stat-time" />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview"><Building2 className="w-4 h-4 mr-2" />Escuelas</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users"><Users className="w-4 h-4 mr-2" />Usuarios</TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts"><ShieldAlert className="w-4 h-4 mr-2" />Incidencias</TabsTrigger>
            <TabsTrigger value="quota-alerts" data-testid="tab-quota-alerts"><Bell className="w-4 h-4 mr-2" />Cupos</TabsTrigger>
            <TabsTrigger value="maintenance" data-testid="tab-maintenance"><Wrench className="w-4 h-4 mr-2" />Mantenimiento</TabsTrigger>
            <TabsTrigger value="schools-admin" data-testid="tab-schools-admin"><AlertTriangle className="w-4 h-4 mr-2" />Crear / Invitar</TabsTrigger>
            <TabsTrigger value="pilot-wizard" data-testid="tab-pilot-wizard"><Sparkles className="w-4 h-4 mr-2" />Piloto</TabsTrigger>
            <TabsTrigger value="curriculum" data-testid="tab-curriculum"><BookOpen className="w-4 h-4 mr-2" />Currículo</TabsTrigger>
            <TabsTrigger value="contact" data-testid="tab-contact"><Inbox className="w-4 h-4 mr-2" />Solicitudes</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages"><MessageSquare className="w-4 h-4 mr-2" />Mensajes</TabsTrigger>
            <TabsTrigger value="ai-escalations" data-testid="tab-ai-escalations"><Bot className="w-4 h-4 mr-2" />Escalamientos IA</TabsTrigger>
            <TabsTrigger value="schools-trash" data-testid="tab-schools-trash"><Archive className="w-4 h-4 mr-2" />Papelera</TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security"><ShieldAlert className="w-4 h-4 mr-2" />Seguridad</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <SchoolBreakdown rows={schools} onPick={onPickSchool} refresh={fetchStats} />
          </TabsContent>

          <TabsContent value="users">
            <UsersSection
              schools={schools}
              filterSchoolId={filterSchoolId}
              setFilterSchoolId={setFilterSchoolId}
              currentUserId={user?.id}
            />
          </TabsContent>

          <TabsContent value="alerts">
            <IncidentOversightSection schools={schools} currentUserId={user?.id || user?.user_id} />
          </TabsContent>

          <TabsContent value="quota-alerts">
            <QuotaAlertsSection schools={schools} />
          </TabsContent>

          <TabsContent value="maintenance">
            <MaintenanceSection />
          </TabsContent>

          <TabsContent value="schools-admin">
            <SchoolsSection schools={schools} refresh={fetchStats} />
          </TabsContent>

          <TabsContent value="pilot-wizard">
            <PilotWizard />
          </TabsContent>

          <TabsContent value="curriculum">
            <CurriculumSection />
          </TabsContent>

          <TabsContent value="contact">
            <ContactSubmissionsSection />
          </TabsContent>

          <TabsContent value="messages">
            <MessagingInbox currentUserRole="super_admin" />
          </TabsContent>

          <TabsContent value="ai-escalations">
            <EscalationAnalyticsPanel />
          </TabsContent>

          <TabsContent value="schools-trash">
            <SchoolsTrashPanel />
          </TabsContent>

          <TabsContent value="security">
            <SecurityOverview />
          </TabsContent>
        </Tabs>
      </main>

      <QHubFooter />
    </div>
  );
};

export default SuperAdminDashboard;
